import { UntitledLoading } from '@payaca/ambrosia';
import { FC, useEffect, useRef, useState } from 'react';

import useGetChatAgentConversation from '@/api/queries/me/useGetChatAgentConversation';
import useGetDocumentTemplate from '@/api/queries/me/useGetDocumentTemplate';

import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import { useChatAgent } from '@/ui/components/chatAgent/ChatAgentContextProvider';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { useHistory } from 'react-router';
import { CreateEditDocumentTemplateForm } from '../components/createEditDocumentTemplate/CreateEditDocumentTemplateForm';
import { CreateTemplateBlueprintPicker } from '../components/createEditDocumentTemplate/CreateTemplateBlueprintPicker';

/**
 * How the create view presents (edit mode ignores this):
 * - 'blueprints': the blueprint table with Dave open alongside (the default)
 * - 'manual': the upload/paste form
 */
export type CreateVariant = 'blueprints' | 'manual';

export const CreateEditDocumentTemplatePage: FC<{
  templateId?: string;
  createVariant?: CreateVariant;
}> = (props) => {
  const { templateId, createVariant = 'blueprints' } = props;
  const history = useHistory();

  const { documentTemplate, isLoading } = useGetDocumentTemplate(templateId);

  const { hasChatAgentAccess, openChatAgentWithBinding, clearBinding } =
    useChatAgent();
  const conversationId = documentTemplate?.chatAgentConversation?.id;
  // Below md the panel renders as a full overlay, so bind without opening
  // there and let the user open it themselves.
  const isMdAndUp = useIsContainerBeyondBreakpoint('md', {
    container: 'document',
  });
  const isEditing = !!templateId;
  const openPanelOnBind = isMdAndUp;

  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!hasChatAgentAccess || isLoading) return;
    openChatAgentWithBinding(
      {
        conversationId,
        greeting: isEditing
          ? [
              "Let's make it your own",
              'What would you like to change about this template?',
            ]
          : [
              'What would you like to create?',
              'Describe the document and Agent Dave will design it for you',
            ],
        newConversationTitle: isEditing ? undefined : 'New template',
        inputPlaceholder: isEditing ? undefined : 'A document for...',
        pageContext: {
          documentTemplateAuthoring: {
            mode: isEditing ? 'edit' : 'create',
            templateId,
            templateName: documentTemplate?.name,
          },
        },
        onActiveConversationChange: setActiveConversationId,
      },
      { open: openPanelOnBind }
    );
  }, [
    hasChatAgentAccess,
    isLoading,
    isEditing,
    templateId,
    documentTemplate?.name,
    conversationId,
    openChatAgentWithBinding,
    openPanelOnBind,
  ]);

  useEffect(() => () => clearBinding(), [clearBinding]);

  // While creating, watch the conversation Dave starts for the template he
  // creates and links, then navigate to its edit view. Report only an
  // unlinked → linked transition: a conversation already linked when first
  // seen belongs to an earlier flow (e.g. the panel still holds an old
  // template's chat) and must not bounce the user to that template.
  const { data: conversationData } = useGetChatAgentConversation(
    isEditing ? null : activeConversationId
  );
  const conversation = conversationData?.chatAgentConversation;

  const unlinkedConversationIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (isEditing || !conversation) return;
    if (!conversation.documentTemplate) {
      unlinkedConversationIdsRef.current.add(conversation.id);
      return;
    }
    if (unlinkedConversationIdsRef.current.has(conversation.id)) {
      unlinkedConversationIdsRef.current.delete(conversation.id);
      history.replace(
        `/templates/document-templates/edit/${conversation.documentTemplate.id}`
      );
    }
  }, [isEditing, conversation, history]);

  if (templateId && !documentTemplate && !isLoading) {
    // The provided templateId does not correspond to a valid template.
    // Navigate back.
    history.goBack();
  }

  // Users without chat agent access get the manual form for every create
  // variant — the others are Dave-led.
  const effectiveVariant: CreateVariant = hasChatAgentAccess
    ? createVariant
    : 'manual';

  const showForm = isEditing || effectiveVariant === 'manual';

  return (
    <>
      <AuthenticatedPageWrapper
        scrollLock
        breadcrumbItems={[
          { label: 'Settings', to: '/companySettings' },
          {
            label: 'Document templates',
            to: '/companySettings/custom-data/document-templates',
          },
          { label: documentTemplate?.name ?? (templateId ? 'Edit' : 'New') },
        ]}
      >
        {isLoading && (
          <div className="w-full h-full flex items-center justify-center">
            {/* TODO MH better styles here */}
            <UntitledLoading />
          </div>
        )}
        {!isLoading && showForm && (
          <CreateEditDocumentTemplateForm
            template={documentTemplate}
            onCreated={(newTemplateId) =>
              history.replace(
                `/templates/document-templates/edit/${newTemplateId}`
              )
            }
          />
        )}
        {!isLoading && !showForm && effectiveVariant === 'blueprints' && (
          <CreateTemplateBlueprintPicker />
        )}
      </AuthenticatedPageWrapper>
    </>
  );
};
